import { QueryObserverResult, RefetchOptions, RefetchQueryFilters, useQuery } from "@tanstack/react-query"
import ResponseError from "../../types/responseError"
import buildCarrotApiRetryPolicy from "../../utils/CarrotApiRetryPolicy"
import useQueryExpirationHandler from "../../components/context/authentication/hooks/useQueryExpirationHandler"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import { carrotClient } from "#/utils/CarrotClient"
import { HttpStatusCodes } from "#/utils/HttpStatusCodes"

const ENDPOINT = "GetChannelId"

export function getChannelIdQueryKey(): string[] {
  return [ENDPOINT]
}

export type UseChannelIdReturn = {
  data: number
  error: ResponseError
  isError: boolean
  isLoading: boolean
  refetch: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<number, ResponseError>>
  hasChannelId: boolean
}

export default function useChannelId(): UseChannelIdReturn {
  const channelIdQuery = useQuery<number, ResponseError>(getChannelIdQueryKey(), () => carrotClient.getChannelId(), {
    refetchOnWindowFocus: false,
    retry: buildCarrotApiRetryPolicy(3),
    onError: (error) => {
      // 401 and 403 (e.g. an admin or consumer without the Employee/Partner role) are expected and handled by
      // useQueryExpirationHandler below, so they aren't reported
      if (error?.statusCode === HttpStatusCodes.UNAUTHORIZED || error?.statusCode === HttpStatusCodes.FORBIDDEN) {
        return
      }
      reportErrorMessage(`Could not load channelID for current employee. Error: ${error}`, {
        tags: { statusCode: String(error?.statusCode), correlationId: error?.correlationId }
      })
    }
  })
  useQueryExpirationHandler(channelIdQuery)
  const { data, isLoading, isError, error, refetch } = channelIdQuery
  return {
    data,
    error,
    isLoading,
    isError,
    refetch,
    hasChannelId: Boolean(channelIdQuery.data)
  }
}
